import React from 'react';
import { motion } from 'framer-motion';
import { Finding, ReviewStatus } from '../../data/pillars';
import { fadeUp } from '../../utils/motion';
type FindingExampleProps = {
  pillarName: string;
  finding: Finding;
};
const statusClasses: Record<ReviewStatus, string> = {
  Reviewed: 'bg-teal/10 text-teal border-teal/30',
  'Awaiting review': 'bg-surface-muted text-navy border-line',
  'Insufficient evidence': 'bg-white text-ink-muted border-dashed border-ink-muted/50'
};
export function FindingExample({
  pillarName,
  finding
}: FindingExampleProps) {
  const draft = finding.reviewStatus === 'Awaiting review';
  const draftLabel = 'Draft interpretation—awaiting human review';
  const rows: {
    label: string;
    value: string;
  }[] = [{
    label: 'Response excerpt',
    value: finding.excerpt
  }, {
    label: draft ? `Observed reasoning · ${draftLabel}` : 'Observed reasoning',
    value: finding.observedReasoning
  }, {
    label: draft ? `Rubric explanation · ${draftLabel}` : 'Rubric explanation',
    value: finding.rubric
  }, {
    label: 'Evidence limitations',
    value: finding.limitations
  }, {
    label: 'Development activity',
    value: finding.activity
  }, {
    label: 'Reflection',
    value: finding.reflection
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10" data-id="element-1615" data-mp="g8txthtcvjh">
      <motion.article {...fadeUp} aria-label="Illustrative finding" className="lg:col-span-8 bg-white border border-line rounded-lg overflow-hidden" data-id="element-1616" data-mp="3rkz367rw8rgn">
        <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-line bg-surface-bg" data-id="element-1617" data-mp="4gslmhjmb6y8">
          <div data-id="element-1618" data-mp="j5yk23amst52">
            <p className="text-xs font-medium text-ink-muted" data-id="element-1619" data-mp="j4njsdgea3ip">{pillarName}</p>
            <p className="font-heading font-semibold text-ink-dark" data-id="element-1620" data-mp="e62i09lhvbs5">{finding.task}</p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border whitespace-nowrap ${statusClasses[finding.reviewStatus]}`} data-id="element-1621" data-mp="s5vb2bkhu43a">
            {finding.reviewStatus}
          </span>
        </header>
        {draft && <p className="px-6 py-3 border-b border-line bg-lavender-pale text-sm font-semibold text-ink-dark" data-id="element-1622" data-mp="v9jcugqv6vem">
            {draftLabel}. The interpretation below is not a finalized finding.
          </p>}
        <dl className="divide-y divide-line" data-id="element-1623" data-mp="154urz066q4mw">
          {rows.map(row => <div key={row.label} className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-6 px-6 py-4" data-id="element-1624" data-mp="19xnwbmxqrayg">
              <dt className="text-sm font-medium text-ink-muted" data-id="element-1625" data-mp="1dxy67aonmlil">{row.label}</dt>
              <dd className="text-ink-dark leading-relaxed" data-id="element-1626" data-mp="1iw9cxcjnkfwl">{row.value}</dd>
            </div>)}
          <div className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-6 px-6 py-4" data-id="element-1627" data-mp="1nxyoquep8xlq">
            <dt className="text-sm font-medium text-ink-muted" data-id="element-1628" data-mp="1sufetu5glm1g">Review history</dt>
            <dd data-id="element-1629" data-mp="3posc43uxij4a">
              <ol className="space-y-1.5" data-id="element-1630" data-mp="3ppbqxbpa8re3">
                {finding.history.map(entry => <li key={entry.date + entry.event} className="text-ink-dark" data-id="element-1631" data-mp="3unwhskin9ypv">
                    <span className="text-sm text-ink-muted mr-3" data-id="element-1632" data-mp="2bs8rw1o1cqx5">{entry.date}</span>
                    {entry.event}
                  </li>)}
              </ol>
            </dd>
          </div>
        </dl>
        <p className="px-6 py-3 border-t border-line bg-surface-bg text-xs text-ink-muted" data-id="element-1633" data-mp="230gno8plue10">
          Fictional example for illustration. Not a real person or a validated result.
        </p>
      </motion.article>

      <motion.aside {...fadeUp} aria-label="Insufficient evidence example" className="lg:col-span-4" data-id="element-1634" data-mp="2uq1x5am4tcxp">
        <div className="border border-dashed border-ink-muted/50 rounded-lg p-6 bg-white" data-id="element-1635" data-mp="2qq152n641386">
          <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-md border ${statusClasses['Insufficient evidence']}`} data-id="element-1636" data-mp="2lolob7aytivl">
            Insufficient evidence
          </span>
          <p className="mt-4 text-ink-dark leading-relaxed" data-id="element-1637" data-mp="1absza49k78j8">
            When a task does not give enough evidence to support an interpretation, the finding says so.
          </p>
          <p className="mt-3 text-ink-muted leading-relaxed" data-id="element-1638" data-mp="2t8ct8n1libl6">
            Missing evidence is not the same as low performance. It is shown as its own state, and may lead to a
            further task rather than a development activity.
          </p>
        </div>
        <p className="mt-5 text-sm text-ink-muted leading-relaxed" data-id="element-1639" data-mp="2p70xs0w1iw52">
          Results are presented as a five-pillar profile. The current model has no overall intelligence score, civic
          rank or eligibility rating; any such summary is a proposed future use that would require validation.
        </p>
      </motion.aside>
    </div>;
}